import React from 'react';
import styled, { keyframes } from 'styled-components';
import Home from '../Home';
import CustomSEO2 from './CustomSEO2';
import CustomSEO3 from './CustomSEO3';

// --- ANIMATIONS ---
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// --- STYLED COMPONENTS ---
const PageContainer = styled.div`
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #333;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, 
    rgba(10, 25, 47, 0.9) 0%,
    rgba(32, 82, 149, 0.8) 50%,
    rgba(15, 35, 65, 0.9) 100%),
    url('placeholder-seo-bg.jpg') center center no-repeat;
  background-size: cover;
  padding: 100px 5%;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 80vh;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.03' fill-rule='evenodd'/%3E%3C/svg%3E");
    opacity: 0.3;
  }

  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
    padding: 80px 5%;
    min-height: auto;
  }
`;

const HeroContent = styled.div`
  max-width: 55%;
  text-align: left;
  animation: ${fadeInUp} 1s ease-out;

  @media (max-width: 992px) {
    max-width: 100%;
    margin-bottom: 40px;
    text-align: center;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.2em;
  margin-bottom: 15px;
  line-height: 1.2;
  font-weight: 700;
  background: linear-gradient(135deg, #ffffff 0%, #f7a61d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5em;
  }
`;

const CompanyTagline = styled.h2`
  font-size: 1.8em;
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 25px;
  color: #f0f0f0;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #f7a61d, transparent);
    
    @media (max-width: 992px) {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const HeroParagraph = styled.p`
  font-size: 1.1em;
  line-height: 1.7;
  margin-bottom: 35px;
  color: #e0e0e0;
`;

const ClickHereButton = styled.button`
  background: linear-gradient(135deg, #f7a61d 0%, #e0971a 100%);
  color: white;
  border: none;
  padding: 15px 35px;
  font-size: 1.1em;
  font-weight: bold;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(247, 166, 29, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(247, 166, 29, 0.4);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const HelpButton = styled.a`
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  padding: 18px 35px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 1.2em;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(0, 123, 255, 0.3);
  display: inline-block;
  animation: ${float} 3s ease-in-out infinite;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 123, 255, 0.4);
    animation: none;
    
    &::before {
      left: 100%;
    }
  }

  @media (max-width: 992px) {
    width: auto;
    text-align: center;
  }
`;

const HeroImageArea = styled.div`
  position: relative;
  animation: ${fadeInUp} 1s ease-out 0.2s both;

  @media (max-width: 992px) {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

const SEOBlocks = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  justify-content: center;
`;

const SEOBlock = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2em;
  font-weight: bold;
  border-radius: 10px;
  background: ${props => props.color};
  color: white;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  animation: ${pulse} 2s infinite;
  animation-delay: ${props => props.delay || '0s'};
`;

const WhyNeedSection = styled.section`
  padding: 80px 5%;
  background: linear-gradient(135deg, #f9f9f9 0%, #ffffff 50%, #f5f5f5 100%);
  text-align: center;
  position: relative;
`;

const SectionHeader = styled.h2`
  font-size: 2.5em;
  color: #333;
  position: relative;
  margin-bottom: 60px;
  font-weight: 700;

  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #f7a61d, #007bff);
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: -15px;
    border-radius: 2px;
  }
`;

const CardContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const Card = styled.div`
  background: white;
  border: 1px solid #eaeaea;
  border-top: 5px solid ${props => props.color || '#007bff'};
  padding: 30px 25px;
  width: 100%;
  max-width: 350px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  text-align: left;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.color || '#007bff'};
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);

    &::before {
      transform: scaleX(1);
    }
  }

  @media (max-width: 768px) {
    width: 90%;
    margin-bottom: 20px;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.5em;
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
  text-align: center;
  font-weight: 600;
`;

const IconPlaceholder = styled.div`
  text-align: center;
  margin-bottom: 15px;
  font-size: 2.5em;
  animation: ${float} 3s ease-in-out infinite;
`;

const SubtitleBox = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  color: #495057;
  padding: 12px 15px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 600;
  border-radius: 6px;
  border-left: 4px solid ${props => props.color || '#007bff'};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ListItem = styled.li`
  font-size: 0.95em;
  margin-bottom: 12px;
  line-height: 1.5;
  position: relative;
  padding-left: 25px;
  color: #555;

  &::before {
    content: '✓';
    color: ${props => props.color || '#f7a61d'};
    font-weight: bold;
    display: inline-block;
    width: 1em;
    margin-left: -1em;
    position: absolute;
    left: 0;
    background: ${props => props.bgColor || 'rgba(247, 166, 29, 0.1)'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    text-align: center;
    line-height: 20px;
    font-size: 0.8em;
  }
`;

// --- REACT COMPONENT ---
const CustomSEO = () => {
  return (
    <Home>
      <PageContainer>
        <HeroSection>
          <HeroContent>
            <HeroTitle>
              Custom SEO: <br />
              Best SEO Company in Patna
            </HeroTitle>
            <CompanyTagline>SRK Software Services Company That Gets It</CompanyTagline>
            <HeroParagraph>
              Are you searching for <strong>Best SEO company in Patna</strong>? Don't miss out on valuable leads by neglecting search engine optimization (<strong>SEO</strong>). It's not just an option anymore, it's essential for expanding your business. <strong>Custom SEO solutions for websites and businesses are paramount to success</strong>. Whether you're selling products or services, increasing qualified leads, or boosting sales, a robust SEO strategy is a must-have. Are you in need of <strong>the best SEO services</strong> near me or looking for <strong>best Search Engine Optimisation services</strong>? Investing in tailored SEO strategies is vital to ensure your business succeeds and dominates the online market.
            </HeroParagraph>
            <ClickHereButton>GET STARTED TODAY</ClickHereButton>
          </HeroContent>
          <HeroImageArea>
            <SEOBlocks>
              <SEOBlock color="#007bff" delay="0s">S</SEOBlock>
              <SEOBlock color="#f7a61d" delay="0.2s">E</SEOBlock>
              <SEOBlock color="#28a745" delay="0.4s">O</SEOBlock>
            </SEOBlocks>
            <HelpButton href="#contact">Need help with Best SEO?</HelpButton>
          </HeroImageArea>
        </HeroSection>

        <WhyNeedSection>
          <SectionHeader>Why Do You Need Custom SEO For Your Website?</SectionHeader>
          <CardContainer>
            <Card color="#007bff">
              <CardTitle>Online Presence</CardTitle>
              <IconPlaceholder>
                <span role="img" aria-label="Visibility icon">👁️</span>
              </IconPlaceholder>
              <SubtitleBox color="#007bff">Your website isn't on the first page</SubtitleBox>
              <List>
                <ListItem color="#007bff" bgColor="rgba(0, 123, 255, 0.1)">Search ranking needs improvement.</ListItem>
                <ListItem color="#007bff" bgColor="rgba(0, 123, 255, 0.1)">Limited visibility affects reach.</ListItem>
                <ListItem color="#007bff" bgColor="rgba(0, 123, 255, 0.1)">Struggling to stand out.</ListItem>
                <ListItem color="#007bff" bgColor="rgba(0, 123, 255, 0.1)">Social media engagement is lacking.</ListItem>
                <ListItem color="#007bff" bgColor="rgba(0, 123, 255, 0.1)">Competitors outshining online presence.</ListItem>
              </List>
            </Card>

            <Card color="#f7a61d">
              <CardTitle>Website Traffic</CardTitle>
              <IconPlaceholder>
                <span role="img" aria-label="Traffic icon">🚀</span>
              </IconPlaceholder>
              <SubtitleBox color="#f7a61d">Your website traffic has diminished</SubtitleBox>
              <List>
                <ListItem color="#f7a61d" bgColor="rgba(247, 166, 29, 0.1)">Decreased website visits noticed.</ListItem>
                <ListItem color="#f7a61d" bgColor="rgba(247, 166, 29, 0.1)">Traffic drop impacting engagement.</ListItem>
                <ListItem color="#f7a61d" bgColor="rgba(247, 166, 29, 0.1)">Need strategies for boosting traffic.</ListItem>
                <ListItem color="#f7a61d" bgColor="rgba(247, 166, 29, 0.1)">Analytics show declining web visitors.</ListItem>
                <ListItem color="#f7a61d" bgColor="rgba(247, 166, 29, 0.1)">Conversion rate affected by traffic.</ListItem>
              </List>
            </Card>

            <Card color="#dc3545">
              <CardTitle>Leads Declining</CardTitle>
              <IconPlaceholder>
                <span role="img" aria-label="Leads declining icon">📉</span>
              </IconPlaceholder>
              <SubtitleBox color="#dc3545">Volume of leads has declined</SubtitleBox>
              <List>
                <ListItem color="#dc3545" bgColor="rgba(220, 53, 69, 0.1)">Fewer inquiries coming in.</ListItem>
                <ListItem color="#dc3545" bgColor="rgba(220, 53, 69, 0.1)">Lead generation efforts not delivering.</ListItem>
                <ListItem color="#dc3545" bgColor="rgba(220, 53, 69, 0.1)">Conversion rate showing decline.</ListItem>
                <ListItem color="#dc3545" bgColor="rgba(220, 53, 69, 0.1)">Decreasing volume of potential customers.</ListItem>
                <ListItem color="#dc3545" bgColor="rgba(220, 53, 69, 0.1)">Demand for products/services dropping.</ListItem>
              </List>
            </Card>
          </CardContainer>
        </WhyNeedSection>

        <CustomSEO2 />
        <CustomSEO3 />
      </PageContainer>
    </Home>
  );
};

export default CustomSEO;